import React, { useRef, useState } from 'react';
import { X, Download, Loader2 } from 'lucide-react';
import {
  REPORT_PERIOD, REPORT_ORG,
  MITRE_TACTICS,
  POSTURE_DOMAINS, OVERALL_POSTURE_SCORE,
  EXEC_POSTURE_PILLARS, EXEC_POSTURE_ROI,
  EXEC_POSTURE_ROADMAP,
  INCIDENT_PERIOD, INCIDENT_WEEK,
  INCIDENT_SEVERITY_BREAKDOWN, INCIDENT_CATEGORY_BREAKDOWN,
  TOP_INCIDENTS, INCIDENT_RESPONSE_METRICS, WEEKLY_INCIDENT_TREND,
  COMPLIANCE_PERIOD, COMPLIANCE_FRAMEWORKS, NIST_FUNCTIONS,
  COMPLIANCE_FINDINGS, COMPLIANCE_REMEDIATION_ROADMAP,
  EXEC_THREAT_CATEGORIES, EXEC_SECURITY_HEALTH,
  EXEC_NOTABLE_EVENTS, EXEC_NEXT_STEPS,
} from '../mitre-data';

// ─── Constants ────────────────────────────────────────────────────────────────

const TODAY = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

const SEV_COLOR  = { Critical: '#dc2626', High: '#ea580c', Medium: '#ca8a04', Low: '#16a34a' };

// ─── Shared Print-Only Components ─────────────────────────────────────────────

function Cover({ title, subtitle, accentColor = '#059669', meta = [] }) {
  return (
    <div className="pdf-cover">
      {/* Colour accent bar at the very top */}
      <div className="pdf-cover__accent" style={{ background: accentColor }} />

      <div className="pdf-cover__inner">

        {/* ══ Brand header ═══════════════════════════════════════════════ */}
        <div className="pdf-cover__brand">
          {/* V icon — inlined SVG, green fill, renders correctly in html2canvas */}
          <svg
            className="pdf-cover__brand-icon"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 726.86 602.46"
          >
            <path
              fill="#39ff14"
              d="M725.79,0l1.07,3.04c-19.43,37.8-35.96,77.05-54.65,115.2-2.97,6.07-15.14,32.35-18.86,35.09-1.64,1.2-3.41,1.63-5.43,1.56l-134.42-.04,71.16,147.13.77,4.12-141.28,295.39-160.97.97-3.5-1.5L0,0h436.07l74.93,150.83L585.93,0h139.86ZM508.5,154.85h-232.77l-100.85,213.31c.65.99,1.28,1.31,2.44,1.49,3.19.51,9.46.87,12.95,1.04,68.01,3.27,138.61-2.58,206.89,0l10.02-1.97,101.33-213.86Z"
            />
          </svg>

          {/* VORXOC wordmark — inlined SVG, black fill, renders correctly in html2canvas */}
          <svg
            className="pdf-cover__brand-wordmark"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 826 149"
          >
            <path fill="#0f172a" d="M56.6395 147.864C54.7311 147.864 53.4382 146.955 52.7656 145.133L0.400751 4.83321C0.133584 3.99821 0 3.43336 0 3.14356C0 1.74371 1.08775 1.04133 3.26326 1.04133H12.6332C13.7114 1.04133 14.5606 1.25745 15.1713 1.68968C15.782 2.1121 16.2256 2.80465 16.5071 3.77227L61.7347 126.252H62.7462L107.974 3.77227C108.518 1.95492 109.811 1.04133 111.848 1.04133H121.828C123.594 1.04133 124.481 1.81739 124.481 3.35968L124.061 5.02968L71.7154 145.133C71.0283 146.955 69.807 147.864 68.0513 147.864H56.6395Z"/>
            <path fill="#0f172a" d="M180.285 148.905C169.279 148.905 160.887 145.938 155.114 140.005C149.351 134.062 146.47 125.422 146.47 114.09V34.8146C146.47 23.3554 149.351 14.6911 155.114 8.82156C160.887 2.94219 169.279 4.95911e-05 180.285 4.95911e-05H228.147C239.291 4.95911e-05 247.745 2.94219 253.508 8.82156C259.281 14.6911 262.172 23.3554 262.172 34.8146V114.09C262.172 125.422 259.281 134.062 253.508 140.005C247.745 145.938 239.291 148.905 228.147 148.905H180.285ZM226.925 134.229C233.309 134.229 238.132 132.554 241.39 129.199C244.644 125.849 246.276 120.883 246.276 114.306V34.5985C246.276 21.3171 239.826 14.6764 226.925 14.6764H181.698C168.797 14.6764 162.347 21.3171 162.347 34.5985V114.306C162.347 120.883 163.974 125.849 167.232 129.199C170.5 132.554 175.324 134.229 181.698 134.229H226.925Z"/>
            <path fill="#0f172a" d="M411.073 143.246C411.746 144.229 412.085 145.069 412.085 145.761C412.085 147.166 411.207 147.864 409.451 147.864H397.619C395.721 147.864 394.094 147.097 392.734 145.565L338.346 82.0067H319.816V145.133C319.816 146.955 318.862 147.864 316.954 147.864H306.573C304.803 147.864 303.92 146.955 303.92 145.133V3.77227C303.92 1.95492 304.803 1.04133 306.573 1.04133H375.006C386.417 1.04133 394.738 3.73297 399.967 9.11626C405.195 14.4995 407.81 23.0656 407.81 34.8146V48.2335C407.81 59.9824 405.195 68.5485 399.967 73.9318C394.738 79.3151 386.417 82.0067 375.006 82.0067H359.529V83.048L411.073 143.246ZM319.816 15.7373V67.3304H373.994C380.239 67.3304 384.819 65.8323 387.734 62.8312C390.659 59.8203 392.123 55.1001 392.123 48.6657V34.402C392.123 27.9725 390.659 23.2572 387.734 20.2561C384.819 17.2452 380.239 15.7373 373.994 15.7373H319.816Z"/>
            <path fill="#0f172a" d="M428.575 147.864C426.677 147.864 425.732 147.097 425.732 145.565C425.732 144.858 425.932 144.224 426.343 143.659L472.792 72.7726L428.575 5.24579C428.027 4.68585 427.755 3.98347 427.755 3.14356C427.755 1.74371 428.637 1.04133 430.407 1.04133H440.808C441.886 1.04133 442.735 1.21816 443.346 1.5718C443.957 1.92545 444.605 2.52468 445.292 3.35968L481.341 58.9411H482.161L518.21 3.35968C518.897 2.52468 519.546 1.92545 520.157 1.5718C520.767 1.21816 521.612 1.04133 522.695 1.04133H532.885C533.696 1.04133 534.374 1.25745 534.908 1.68968C535.452 2.1121 535.729 2.66712 535.729 3.35968C535.729 3.92453 535.523 4.55324 535.118 5.24579L490.711 72.7726L536.95 143.659C537.355 144.224 537.561 144.926 537.561 145.761C537.561 147.166 536.745 147.864 535.118 147.864H524.737C523.639 147.864 522.757 147.697 522.084 147.353C521.406 146.999 520.729 146.331 520.042 145.349L482.161 86.8399H481.341L468.307 106.958L443.25 145.349C442.296 147.029 440.798 147.864 438.766 147.864H428.575Z"/>
            <path fill="#0f172a" d="M592.77 148.905C581.764 148.905 573.372 145.938 567.599 140.005C561.836 134.061 558.954 125.422 558.954 114.09V34.8145C558.954 23.3554 561.836 14.6911 567.599 8.82151C573.372 2.94214 581.764 0 592.77 0H640.631C651.776 0 660.23 2.94214 665.993 8.82151C671.766 14.6911 674.657 23.3554 674.657 34.8145V114.09C674.657 125.422 671.766 134.061 665.993 140.005C660.23 145.938 651.776 148.905 640.631 148.905H592.77ZM639.41 134.228C645.793 134.228 650.616 132.554 653.875 129.199C657.129 125.849 658.76 120.883 658.76 114.306V34.5984C658.76 21.317 652.31 14.6763 639.41 14.6763H594.182C581.282 14.6763 574.831 21.317 574.831 34.5984V114.306C574.831 120.883 576.458 125.849 579.717 129.199C582.985 132.554 587.808 134.228 594.182 134.228H639.41Z"/>
            <path fill="#0f172a" d="M749.209 148.905C738.202 148.905 729.811 145.938 724.038 140.005C718.275 134.061 715.393 125.422 715.393 114.09V34.8145C715.393 23.3554 718.275 14.6911 724.038 8.82151C729.811 2.94214 738.202 0 749.209 0H791.975C803.119 0 811.573 2.94214 817.336 8.82151C823.109 14.6911 826 23.3554 826 34.8145V43.8325C826 45.6548 825.051 46.5634 823.157 46.5634H812.756C810.987 46.5634 810.104 45.6548 810.104 43.8325V34.5984C810.104 21.317 803.654 14.6763 790.753 14.6763H750.621C737.721 14.6763 731.27 21.317 731.27 34.5984V114.306C731.27 120.883 732.897 125.849 736.156 129.199C739.424 132.554 744.247 134.228 750.621 134.228H790.753C797.137 134.228 801.96 132.554 805.219 129.199C808.472 125.849 810.104 120.883 810.104 114.306V105.072C810.104 103.122 810.987 102.145 812.756 102.145H823.157C825.051 102.145 826 103.122 826 105.072V114.09C826 125.422 823.109 134.061 817.336 140.005C811.573 145.938 803.119 148.905 791.975 148.905H749.209Z"/>
          </svg>

          {/* Vertical divider */}
          <div className="pdf-cover__brand-divider" />
          <span className="pdf-cover__tagline">SECURITY INTELLIGENCE PLATFORM</span>
        </div>

        {/* ══ Report title ════════════════════════════════════════════════ */}
        <div className="pdf-cover__titles">
          <h1 className="pdf-cover__title">{title}</h1>
          <p className="pdf-cover__subtitle">{subtitle}</p>
        </div>

        {/* ══ Meta information grid ════════════════════════════════════════ */}
        <div className="pdf-cover__meta-grid">
          {meta.map((row, i) => (
            <div key={i} className="pdf-cover__meta-cell">
              <span className="pdf-cover__meta-key">{row.label}</span>
              <span className="pdf-cover__meta-val">{row.value}</span>
            </div>
          ))}
        </div>

        {/* ══ Classification stamp ═════════════════════════════════════════ */}
        <div className="pdf-cover__confidential">
          <span className="pdf-cover__confidential-dot" />
          CONFIDENTIAL — RESTRICTED DISTRIBUTION
        </div>
      </div>
    </div>
  );
}

function Section({ num, title, children }) {
  return (
    <div className="pdf-section">
      <div className="pdf-section__hdr">
        <span className="pdf-section__num">{num}</span>
        <h2 className="pdf-section__title">{title}</h2>
      </div>
      <div className="pdf-section__body">{children}</div>
    </div>
  );
}

function KpiGrid({ kpis }) {
  return (
    <div className="pdf-kpi-grid">
      {kpis.map((k, i) => (
        <div key={i} className="pdf-kpi" style={{ borderTopColor: k.color || '#059669' }}>
          <div className="pdf-kpi__value" style={{ color: k.color || '#0f172a' }}>{k.value}</div>
          <div className="pdf-kpi__label">{k.label}</div>
          {k.sub && <div className="pdf-kpi__sub">{k.sub}</div>}
        </div>
      ))}
    </div>
  );
}

function InlineBar({ pct, color = '#059669', height = 7 }) {
  return (
    <div style={{ background: '#e2e8f0', borderRadius: 4, height, overflow: 'hidden', minWidth: 80 }}>
      <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: 4 }} />
    </div>
  );
}

function SevBadge({ sev }) {
  return (
    <span style={{
      display: 'inline-block', padding: '2px 7px', borderRadius: 4,
      fontSize: 10, fontWeight: 700,
      color: SEV_COLOR[sev] || '#475569',
      background: `${SEV_COLOR[sev] || '#475569'}15`,
      border: `1px solid ${SEV_COLOR[sev] || '#475569'}40`,
    }}>{sev}</span>
  );
}

function Footer({ org }) {
  return (
    <div className="pdf-footer">
      <span>VorXOC Security Intelligence Platform — {org} — Confidential</span>
      <span>Generated {TODAY}</span>
    </div>
  );
}

// ─── 1. MITRE ATT&CK Executive Threat Assessment ──────────────────────────────

const STATUS_CONFIG = {
  protected:  { label: 'Protected',   bg: '#dcfce7', color: '#166534', border: '#bbf7d0', dot: '#059669' },
  monitoring: { label: 'Monitoring',  bg: '#fef9c3', color: '#854d0e', border: '#fde68a', dot: '#ca8a04' },
  gap:        { label: 'Gap — Action Required', bg: '#fee2e2', color: '#991b1b', border: '#fecaca', dot: '#dc2626' },
};

function ThreatStatusPill({ status }) {
  const s = STATUS_CONFIG[status] || STATUS_CONFIG.monitoring;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700,
      background: s.bg, color: s.color, border: `1px solid ${s.border}`,
    }}>
      <span style={{ width: 7, height: 7, borderRadius: '50%', background: s.dot, flexShrink: 0 }} />
      {s.label}
    </span>
  );
}

function ThreatCategoryCard({ cat, idx }) {
  const s = STATUS_CONFIG[cat.status] || STATUS_CONFIG.monitoring;
  const totalDet = EXEC_THREAT_CATEGORIES.reduce((a, c) => a + c.detections, 0);
  const pct = Math.round((cat.detections / totalDet) * 100);
  return (
    <div style={{
      border: `1px solid ${s.border}`,
      borderLeft: `5px solid ${s.dot}`,
      borderRadius: 8, marginBottom: 20, overflow: 'hidden', background: '#fff',
    }}>
      {/* Card header */}
      <div style={{ background: '#f8fafc', borderBottom: `1px solid #e2e8f0`, padding: '12px 18px', display: 'flex', alignItems: 'center', gap: 12 }}>
        <span style={{ fontSize: 22 }}>{cat.icon}</span>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <strong style={{ fontSize: 15, color: '#0f172a' }}>{cat.name}</strong>
            <ThreatStatusPill status={cat.status} />
          </div>
          <p style={{ margin: '3px 0 0', fontSize: 12, color: '#64748b' }}>{cat.businessDesc}</p>
        </div>
        <div style={{ textAlign: 'center', flexShrink: 0, minWidth: 70 }}>
          <div style={{ fontSize: 26, fontWeight: 800, color: s.dot, fontFamily: 'monospace' }}>{cat.detections}</div>
          <div style={{ fontSize: 9, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.07em' }}>Threats Detected</div>
          <div style={{ fontSize: 10, color: s.dot, fontWeight: 600 }}>{pct}% of total</div>
        </div>
      </div>

      {/* Two-column body */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0 }}>
        <div style={{ padding: '12px 18px', borderRight: '1px solid #f1f5f9' }}>
          <div style={{ fontSize: 10, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#2563eb', marginBottom: 6 }}>🔍 How We Detect This Threat</div>
          <p style={{ fontSize: 12, color: '#334155', lineHeight: 1.6, margin: 0 }}>{cat.howWeDetect}</p>
        </div>
        <div style={{ padding: '12px 18px' }}>
          <div style={{ fontSize: 10, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#059669', marginBottom: 6 }}>🛡 How We Defend Against It</div>
          <p style={{ fontSize: 12, color: '#334155', lineHeight: 1.6, margin: 0 }}>{cat.howWeDefend}</p>
        </div>
      </div>

      {/* Examples this period */}
      <div style={{ padding: '8px 18px 12px', borderTop: '1px solid #f1f5f9', background: '#fafbfc' }}>
        <div style={{ fontSize: 10, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#475569', marginBottom: 6 }}>Examples from This Reporting Period</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {cat.topThreats.map((t, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 6, fontSize: 12, color: '#475569' }}>
              <span style={{ color: s.dot, fontWeight: 700, flexShrink: 0, marginTop: 1 }}>›</span>
              {t}
            </div>
          ))}
        </div>
      </div>

      {/* Business risk */}
      {cat.status === 'gap' && (
        <div style={{ padding: '8px 18px', borderTop: '1px solid #fecaca', background: '#fff7f7' }}>
          <span style={{ fontSize: 11, color: '#dc2626', fontWeight: 600 }}>⚠ Business Risk: </span>
          <span style={{ fontSize: 11, color: '#7f1d1d' }}>{cat.businessRisk}</span>
        </div>
      )}
    </div>
  );
}

function MitreAssessmentReport() {
  const h = EXEC_SECURITY_HEALTH;
  const totalDet = EXEC_THREAT_CATEGORIES.reduce((s, c) => s + c.detections, 0);
  const protectedCount  = EXEC_THREAT_CATEGORIES.filter(c => c.status === 'protected').length;
  const monitoringCount = EXEC_THREAT_CATEGORIES.filter(c => c.status === 'monitoring').length;
  const gapCount        = EXEC_THREAT_CATEGORIES.filter(c => c.status === 'gap').length;

  return (
    <div className="pdf-report">
      <Cover
        title="Security Threat Assessment"
        subtitle={`How We Detect & Defend Against Cyber Threats · ${REPORT_PERIOD}`}
        accentColor="#f97316"
        meta={[
          { label: 'Prepared for',     value: `${REPORT_ORG} — Executive Leadership` },
          { label: 'Reporting Period', value: REPORT_PERIOD },
          { label: 'Prepared by',      value: 'VorXOC Security Operations Centre' },
          { label: 'Security Rating',  value: `${h.overallRating} (${h.overallScore}/100)` },
          { label: 'Generated',        value: TODAY },
          { label: 'Classification',   value: 'Confidential' },
        ]}
      />

      {/* ── Section 1: Security Health ── */}
      <Section num="1" title="Your Security at a Glance">
        <div style={{
          background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
          borderRadius: 10, padding: '20px 24px', marginBottom: 20, color: '#fff',
        }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 20, marginBottom: 16 }}>
            <div>
              <div style={{ fontSize: 44, fontWeight: 900, fontFamily: 'monospace', color: '#34d399', lineHeight: 1 }}>{h.overallScore}</div>
              <div style={{ fontSize: 11, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.1em' }}>Security Score</div>
            </div>
            <div style={{ flex: 1, borderLeft: '1px solid rgba(255,255,255,0.1)', paddingLeft: 20 }}>
              <p style={{ fontSize: 13, color: '#e2e8f0', lineHeight: 1.65, margin: 0 }}>{h.statement}</p>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            {[
              { label: 'Threats Stopped',       value: h.threatsBlocked.toLocaleString(), color: '#34d399' },
              { label: 'Detection Coverage',    value: h.coverageRate,    color: '#34d399' },
              { label: 'Incidents Resolved',    value: h.incidentResolutionRate, color: '#34d399' },
              { label: 'Avg Response Time',     value: h.avgResponseTime, color: '#fbbf24' },
              { label: 'Coverage Gaps',         value: `${h.criticalGaps} area`,  color: '#f87171' },
            ].map((k, i) => (
              <div key={i} style={{ flex: 1, minWidth: 90, background: 'rgba(255,255,255,0.06)', borderRadius: 6, padding: '10px 12px' }}>
                <div style={{ fontSize: 18, fontWeight: 800, color: k.color, fontFamily: 'monospace' }}>{k.value}</div>
                <div style={{ fontSize: 9, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.07em', marginTop: 3 }}>{k.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Status summary */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
          <div style={{ background: '#dcfce7', border: '1px solid #bbf7d0', borderRadius: 8, padding: '14px 16px', textAlign: 'center' }}>
            <div style={{ fontSize: 28, fontWeight: 900, color: '#166534', fontFamily: 'monospace' }}>{protectedCount}</div>
            <div style={{ fontSize: 11, color: '#166534', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Threat Categories</div>
            <div style={{ fontSize: 12, color: '#166534' }}>✓ Fully Protected</div>
          </div>
          <div style={{ background: '#fef9c3', border: '1px solid #fde68a', borderRadius: 8, padding: '14px 16px', textAlign: 'center' }}>
            <div style={{ fontSize: 28, fontWeight: 900, color: '#854d0e', fontFamily: 'monospace' }}>{monitoringCount}</div>
            <div style={{ fontSize: 11, color: '#854d0e', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Threat Categories</div>
            <div style={{ fontSize: 12, color: '#854d0e' }}>⚡ Actively Monitored</div>
          </div>
          <div style={{ background: '#fee2e2', border: '1px solid #fecaca', borderRadius: 8, padding: '14px 16px', textAlign: 'center' }}>
            <div style={{ fontSize: 28, fontWeight: 900, color: '#991b1b', fontFamily: 'monospace' }}>{gapCount}</div>
            <div style={{ fontSize: 11, color: '#991b1b', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Threat Categories</div>
            <div style={{ fontSize: 12, color: '#991b1b' }}>⚠ Gap — Action Needed</div>
          </div>
        </div>
      </Section>

      {/* ── Section 2: Threat Category Overview ── */}
      <Section num="2" title="Threat Landscape Overview">
        <p className="pdf-para">
          During <strong>{REPORT_PERIOD}</strong>, our security monitoring systems detected{' '}
          <strong>{totalDet} threat events</strong> across 6 threat categories. The table below
          shows each category, how many threats were detected, and your current protection status
          at a glance before we go into detail in the next section.
        </p>
        <table className="pdf-table">
          <thead>
            <tr>
              <th>Threat Category</th>
              <th style={{width:'13%', textAlign:'center'}}>Threats Detected</th>
              <th style={{width:'12%', textAlign:'center'}}>Trend vs Prior Period</th>
              <th style={{width:'22%'}}>Volume</th>
              <th style={{width:'18%', textAlign:'center'}}>Your Protection Status</th>
            </tr>
          </thead>
          <tbody>
            {EXEC_THREAT_CATEGORIES.map((cat, i) => {
              const s = STATUS_CONFIG[cat.status];
              const pct = Math.round((cat.detections / totalDet) * 100);
              const trendColor = cat.trend === 'down' ? '#059669' : cat.trend === 'up' ? '#dc2626' : '#94a3b8';
              const trendArrow = cat.trend === 'down' ? '▼' : cat.trend === 'up' ? '▲' : '—';
              return (
                <tr key={cat.id} style={{ background: i % 2 === 0 ? '#fff' : '#f8fafc' }}>
                  <td>
                    <span style={{ fontSize: 14, marginRight: 6 }}>{cat.icon}</span>
                    <strong style={{ fontSize: 12 }}>{cat.name}</strong>
                  </td>
                  <td style={{ textAlign: 'center', fontWeight: 800, fontSize: 15, fontFamily: 'monospace' }}>{cat.detections}</td>
                  <td style={{ textAlign: 'center', color: trendColor, fontWeight: 700, fontSize: 12 }}>
                    {trendArrow} {cat.trendPct}%
                  </td>
                  <td><InlineBar pct={pct * 4} color={s.dot} height={8} /></td>
                  <td style={{ textAlign: 'center' }}><ThreatStatusPill status={cat.status} /></td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <div style={{ display: 'flex', gap: 16, marginTop: 10, fontSize: 11, color: '#64748b' }}>
          <span>▲ = more threats detected than prior period &nbsp;|&nbsp; ▼ = fewer than prior period</span>
          <span style={{ color: '#dc2626' }}>Red = increase to watch</span>
        </div>
      </Section>

      {/* ── Section 3: Detailed Category Breakdown ── */}
      <Section num="3" title="Threat-by-Threat: How We Detect &amp; Defend">
        <p className="pdf-para">
          For each threat category, we explain in plain language what the threat is, exactly how
          our systems detect it, and what steps we take to protect your organisation. Specific
          examples from this reporting period are included.
        </p>
        {EXEC_THREAT_CATEGORIES.map((cat, i) => (
          <ThreatCategoryCard key={cat.id} cat={cat} idx={i} />
        ))}
      </Section>

      {/* ── Section 4: Notable Events ── */}
      <Section num="4" title="Notable Security Events This Period">
        <p className="pdf-para">
          The following significant events occurred during <strong>{REPORT_PERIOD}</strong>.
          Each summary explains what happened, what we did, and what it means for your organisation.
        </p>
        {EXEC_NOTABLE_EVENTS.map((ev, i) => (
          <div key={i} style={{
            border: '1px solid #e2e8f0', borderRadius: 8, marginBottom: 14, overflow: 'hidden',
          }}>
            <div style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0', padding: '10px 16px', display: 'flex', alignItems: 'center', gap: 12 }}>
              <span style={{ fontSize: 11, color: '#94a3b8', whiteSpace: 'nowrap' }}>{ev.date}</span>
              <strong style={{ flex: 1, fontSize: 13, color: '#0f172a' }}>{ev.title}</strong>
              <span style={{
                fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 12,
                color: ev.color, background: `${ev.color}18`, border: `1px solid ${ev.color}40`,
                whiteSpace: 'nowrap',
              }}>● {ev.outcome}</span>
            </div>
            <div style={{ padding: '12px 16px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div>
                <div style={{ fontSize: 10, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#475569', marginBottom: 5 }}>What Happened</div>
                <p style={{ fontSize: 12, color: '#475569', lineHeight: 1.6, margin: 0 }}>{ev.what}</p>
              </div>
              <div>
                <div style={{ fontSize: 10, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#059669', marginBottom: 5 }}>What We Did</div>
                <p style={{ fontSize: 12, color: '#475569', lineHeight: 1.6, margin: 0 }}>{ev.weDid}</p>
              </div>
            </div>
            <div style={{ padding: '8px 16px', borderTop: '1px solid #f1f5f9', background: '#fafbfc' }}>
              <span style={{ fontSize: 11, color: '#2563eb', fontWeight: 700 }}>📋 Lesson Learned: </span>
              <span style={{ fontSize: 11, color: '#475569' }}>{ev.lesson}</span>
            </div>
          </div>
        ))}
      </Section>

      {/* ── Section 5: Actions Required ── */}
      <Section num="5" title="Actions Required — What Needs to Happen Next">
        <p className="pdf-para">
          The following actions are recommended to improve your security. These are listed in
          priority order. Items marked <strong style={{color:'#dc2626'}}>Urgent</strong> require
          immediate attention from your leadership team.
        </p>
        {EXEC_NEXT_STEPS.map((step, i) => (
          <div key={i} style={{
            border: `1px solid ${step.color}30`,
            borderLeft: `5px solid ${step.color}`,
            borderRadius: 8, padding: '12px 16px', marginBottom: 12, background: '#fff',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
              <span style={{ fontSize: 10, fontWeight: 800, padding: '3px 9px', borderRadius: 12, color: step.color, background: `${step.color}18`, border: `1px solid ${step.color}40`, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{step.priority}</span>
              <strong style={{ fontSize: 14, color: '#0f172a', flex: 1 }}>{step.action}</strong>
              <span style={{ fontSize: 11, color: '#94a3b8', whiteSpace: 'nowrap' }}>Target: {step.when}</span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div>
                <p style={{ fontSize: 12, color: '#334155', lineHeight: 1.6, margin: '0 0 6px' }}><strong>What:</strong> {step.what}</p>
                <p style={{ fontSize: 12, color: '#334155', lineHeight: 1.6, margin: 0 }}><strong>Why:</strong> {step.why}</p>
              </div>
              <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 6, padding: '8px 12px' }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', marginBottom: 4 }}>Cost Estimate</div>
                <p style={{ fontSize: 12, color: '#059669', fontWeight: 600, margin: 0 }}>{step.cost}</p>
              </div>
            </div>
          </div>
        ))}
      </Section>

      {/* ── Section 6: Technical Appendix ── */}
      <Section num="6" title="Appendix: MITRE ATT&amp;CK Technical Reference">
        <p className="pdf-para" style={{ fontSize: 11, color: '#64748b' }}>
          This appendix provides the underlying MITRE ATT&CK framework mapping for technical
          staff and auditors. The MITRE ATT&CK framework is the global standard for cataloguing
          cyber adversary behaviour, developed by MITRE Corporation and used by security teams worldwide.
        </p>
        <table className="pdf-table">
          <thead>
            <tr>
              <th style={{width:'10%'}}>Tactic ID</th>
              <th>Tactic Name</th>
              <th style={{width:'12%', textAlign:'center'}}>Detections</th>
              <th style={{width:'14%', textAlign:'center'}}>Status</th>
              <th>Maps to Threat Category</th>
            </tr>
          </thead>
          <tbody>
            {MITRE_TACTICS.map((t, i) => {
              const catMap = {
                'TA0001': 'Identity & Account Attacks', 'TA0006': 'Identity & Account Attacks',
                'TA0002': 'Malware & Ransomware',       'TA0040': 'Malware & Ransomware',
                'TA0003': 'Privilege & Access Escalation', 'TA0004': 'Privilege & Access Escalation', 'TA0005': 'Privilege & Access Escalation',
                'TA0007': 'Network Reconnaissance',    'TA0008': 'Network Reconnaissance',
                'TA0009': 'Data Theft & Exfiltration', 'TA0010': 'Data Theft & Exfiltration',
                'TA0011': 'Remote Access & C2',
              };
              return (
                <tr key={t.id} style={{ background: !t.covered ? '#fff7f7' : i % 2 === 0 ? '#fff' : '#f8fafc' }}>
                  <td style={{ fontFamily: 'monospace', fontWeight: 700, fontSize: 11, color: '#059669' }}>{t.id}</td>
                  <td style={{ fontWeight: 500 }}>{t.name}</td>
                  <td style={{ textAlign: 'center', fontWeight: 700 }}>{t.detections}</td>
                  <td style={{ textAlign: 'center' }}>
                    <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 4, background: t.covered ? '#dcfce7' : '#fee2e2', color: t.covered ? '#166534' : '#991b1b', border: `1px solid ${t.covered ? '#bbf7d0' : '#fecaca'}` }}>
                      {t.covered ? '✓ Active' : '✗ Gap'}
                    </span>
                  </td>
                  <td style={{ fontSize: 11, color: '#64748b' }}>{catMap[t.id] || '—'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Section>

      <Footer org={REPORT_ORG} />
    </div>
  );
}

// ─── 2. Security Posture Report (Executive) ───────────────────────────────────

function PosturePillarCard({ pillar }) {
  const delta = pillar.score - pillar.benchmark;
  const aheadBehind = delta >= 0
    ? <span style={{ color: '#059669', fontWeight: 700 }}>▲ {delta} pts ahead of industry average</span>
    : <span style={{ color: '#dc2626', fontWeight: 700 }}>▼ {Math.abs(delta)} pts below industry average</span>;

  return (
    <div style={{ border: '1px solid #e2e8f0', borderRadius: 8, marginBottom: 20, overflow: 'hidden', background: '#fff' }}>
      {/* Header */}
      <div style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0', padding: '12px 18px', display: 'flex', alignItems: 'center', gap: 14 }}>
        <span style={{ fontSize: 22 }}>{pillar.icon}</span>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <strong style={{ fontSize: 15, color: '#0f172a' }}>{pillar.name}</strong>
            <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 12, color: pillar.gradeColor, background: `${pillar.gradeColor}18`, border: `1px solid ${pillar.gradeColor}40` }}>{pillar.grade}</span>
          </div>
          <p style={{ margin: '3px 0 0', fontSize: 12, color: '#64748b', fontStyle: 'italic' }}>"{pillar.question}"</p>
        </div>
        <div style={{ textAlign: 'center', flexShrink: 0, minWidth: 80 }}>
          <div style={{ fontSize: 32, fontWeight: 900, color: pillar.gradeColor, fontFamily: 'monospace', lineHeight: 1 }}>{pillar.score}</div>
          <div style={{ fontSize: 9, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.07em' }}>Score / 100</div>
          <div style={{ fontSize: 10, marginTop: 3 }}>{aheadBehind}</div>
        </div>
      </div>

      {/* Score bar vs benchmark */}
      <div style={{ padding: '8px 18px', borderBottom: '1px solid #f1f5f9' }}>
        <div style={{ position: 'relative', height: 10, background: '#e2e8f0', borderRadius: 5, overflow: 'visible' }}>
          <div style={{ position: 'absolute', left: 0, top: 0, height: '100%', width: `${pillar.score}%`, background: pillar.gradeColor, borderRadius: 5 }} />
          <div style={{ position: 'absolute', top: -4, left: `${pillar.benchmark}%`, width: 2, height: 18, background: '#64748b', transform: 'translateX(-50%)', zIndex: 2 }} />
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4, fontSize: 9, color: '#94a3b8' }}>
          <span>0</span>
          <span>Industry average: {pillar.benchmark}</span>
          <span>100</span>
        </div>
      </div>

      {/* Two-column: what it means + what we do */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0 }}>
        <div style={{ padding: '12px 18px', borderRight: '1px solid #f1f5f9' }}>
          <div style={{ fontSize: 10, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#475569', marginBottom: 6 }}>What This Measures</div>
          <p style={{ fontSize: 12, color: '#334155', lineHeight: 1.6, margin: 0 }}>{pillar.whatItMeans}</p>
        </div>
        <div style={{ padding: '12px 18px' }}>
          <div style={{ fontSize: 10, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#059669', marginBottom: 6 }}>What We Do to Achieve This</div>
          <p style={{ fontSize: 12, color: '#334155', lineHeight: 1.6, margin: 0 }}>{pillar.whatWeDo}</p>
        </div>
      </div>

      {/* Wins + gap */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', borderTop: '1px solid #f1f5f9' }}>
        <div style={{ padding: '8px 18px 12px', borderRight: '1px solid #f1f5f9', background: '#f0fdf4' }}>
          <div style={{ fontSize: 10, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#166534', marginBottom: 6 }}>✓ What's Working Well</div>
          {pillar.wins.map((w, i) => (
            <div key={i} style={{ display: 'flex', gap: 5, fontSize: 11, color: '#166534', marginBottom: 3 }}>
              <span style={{ flexShrink: 0 }}>•</span><span>{w}</span>
            </div>
          ))}
        </div>
        <div style={{ padding: '8px 18px 12px', background: pillar.gapColor === '#dc2626' ? '#fff7f7' : '#fffbeb' }}>
          <div style={{ fontSize: 10, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em', color: pillar.gapColor, marginBottom: 6 }}>⚠ Where We Can Improve</div>
          <p style={{ fontSize: 11, color: '#475569', lineHeight: 1.55, margin: 0 }}>{pillar.gap}</p>
        </div>
      </div>
    </div>
  );
}

function SecurityPostureReport() {
  const scoreColor = OVERALL_POSTURE_SCORE >= 80 ? '#059669' : OVERALL_POSTURE_SCORE >= 65 ? '#ca8a04' : '#dc2626';
  const scoreLabel = OVERALL_POSTURE_SCORE >= 80 ? 'Good' : OVERALL_POSTURE_SCORE >= 65 ? 'Good — Room to Improve' : 'At Risk';
  const avgScore   = Math.round(EXEC_POSTURE_PILLARS.reduce((s, p) => s + p.score, 0) / EXEC_POSTURE_PILLARS.length);
  const aboveBench = EXEC_POSTURE_PILLARS.filter(p => p.score >= p.benchmark).length;
  const belowBench = EXEC_POSTURE_PILLARS.length - aboveBench;

  return (
    <div className="pdf-report">
      <Cover
        title="Security Posture Report"
        subtitle={`How Strong Is Our Security? · ${REPORT_PERIOD}`}
        accentColor="#059669"
        meta={[
          { label: 'Prepared for',     value: `${REPORT_ORG} — Executive Leadership` },
          { label: 'Reporting Period', value: REPORT_PERIOD },
          { label: 'Prepared by',      value: 'VorXOC Security Operations Centre' },
          { label: 'Overall Rating',   value: `${scoreLabel} (${avgScore}/100)` },
          { label: 'Generated',        value: TODAY },
          { label: 'Classification',   value: 'Confidential' },
        ]}
      />

      {/* ── Section 1: Scorecard ── */}
      <Section num="1" title="Overall Security Strength">
        <div style={{
          background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
          borderRadius: 10, padding: '20px 24px', marginBottom: 20, color: '#fff',
        }}>
          <div style={{ display: 'flex', gap: 20, marginBottom: 18 }}>
            <div style={{ textAlign: 'center', flexShrink: 0 }}>
              <div style={{ fontSize: 56, fontWeight: 900, fontFamily: 'monospace', color: scoreColor, lineHeight: 1 }}>{avgScore}</div>
              <div style={{ fontSize: 11, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.1em', marginTop: 4 }}>Overall Score</div>
              <div style={{ fontSize: 13, color: scoreColor, fontWeight: 700, marginTop: 2 }}>{scoreLabel}</div>
            </div>
            <div style={{ flex: 1, borderLeft: '1px solid rgba(255,255,255,0.1)', paddingLeft: 20 }}>
              <p style={{ fontSize: 13, color: '#e2e8f0', lineHeight: 1.7, margin: '0 0 14px' }}>
                <strong>{REPORT_ORG}</strong>'s security programme is performing <strong style={{ color: scoreColor }}>above the industry average</strong> in {aboveBench} of 5 key security areas.
                Our biggest strength is <strong style={{ color: '#34d399' }}>Identity & Access Control</strong> (82/100) and <strong style={{ color: '#34d399' }}>Threat Detection</strong> (78/100).
                The area most requiring attention is <strong style={{ color: '#fbbf24' }}>Security Coverage</strong> (67/100), which is 3 points below the industry average.
              </p>
              <div style={{ display: 'flex', gap: 12 }}>
                <div style={{ background: 'rgba(5,150,105,0.15)', border: '1px solid rgba(5,150,105,0.3)', borderRadius: 6, padding: '8px 14px', textAlign: 'center' }}>
                  <div style={{ fontSize: 22, fontWeight: 800, color: '#34d399', fontFamily: 'monospace' }}>{aboveBench}</div>
                  <div style={{ fontSize: 9, color: '#94a3b8', textTransform: 'uppercase' }}>Areas above benchmark</div>
                </div>
                <div style={{ background: 'rgba(220,38,38,0.12)', border: '1px solid rgba(220,38,38,0.25)', borderRadius: 6, padding: '8px 14px', textAlign: 'center' }}>
                  <div style={{ fontSize: 22, fontWeight: 800, color: '#f87171', fontFamily: 'monospace' }}>{belowBench}</div>
                  <div style={{ fontSize: 9, color: '#94a3b8', textTransform: 'uppercase' }}>Areas below benchmark</div>
                </div>
                <div style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 6, padding: '8px 14px', textAlign: 'center' }}>
                  <div style={{ fontSize: 22, fontWeight: 800, color: '#fbbf24', fontFamily: 'monospace' }}>{EXEC_POSTURE_ROI.estimatedValueProtected}</div>
                  <div style={{ fontSize: 9, color: '#94a3b8', textTransform: 'uppercase' }}>Estimated value protected</div>
                </div>
                <div style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 6, padding: '8px 14px', textAlign: 'center' }}>
                  <div style={{ fontSize: 22, fontWeight: 800, color: '#a78bfa', fontFamily: 'monospace' }}>{EXEC_POSTURE_ROI.roi}</div>
                  <div style={{ fontSize: 9, color: '#94a3b8', textTransform: 'uppercase' }}>Return on investment</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Benchmark comparison summary */}
        <p className="pdf-para" style={{ marginBottom: 10 }}>
          The chart below compares our score in each security area against the industry benchmark
          for organisations of similar size and sector. The grey marker shows where the benchmark sits.
        </p>
        <table className="pdf-table">
          <thead>
            <tr>
              <th>Security Area</th>
              <th style={{ width: '9%', textAlign: 'center' }}>Our Score</th>
              <th style={{ width: '9%', textAlign: 'center' }}>Benchmark</th>
              <th style={{ width: '10%', textAlign: 'center' }}>Gap</th>
              <th>Visual Comparison</th>
              <th style={{ width: '14%', textAlign: 'center' }}>Rating</th>
            </tr>
          </thead>
          <tbody>
            {EXEC_POSTURE_PILLARS.map((p, i) => {
              const delta = p.score - p.benchmark;
              return (
                <tr key={p.id} style={{ background: i % 2 === 0 ? '#fff' : '#f8fafc' }}>
                  <td>
                    <span style={{ marginRight: 6 }}>{p.icon}</span>
                    <strong style={{ fontSize: 12 }}>{p.name}</strong>
                  </td>
                  <td style={{ textAlign: 'center', fontWeight: 800, fontSize: 15, fontFamily: 'monospace', color: p.gradeColor }}>{p.score}</td>
                  <td style={{ textAlign: 'center', color: '#94a3b8', fontWeight: 600 }}>{p.benchmark}</td>
                  <td style={{ textAlign: 'center', fontWeight: 700, color: delta >= 0 ? '#059669' : '#dc2626' }}>{delta >= 0 ? '+' : ''}{delta}</td>
                  <td>
                    <div style={{ position: 'relative', height: 8, background: '#e2e8f0', borderRadius: 4 }}>
                      <div style={{ position: 'absolute', left: 0, top: 0, height: '100%', width: `${p.score}%`, background: p.gradeColor, borderRadius: 4 }} />
                      <div style={{ position: 'absolute', top: -3, left: `${p.benchmark}%`, width: 2, height: 14, background: '#64748b', transform: 'translateX(-50%)' }} />
                    </div>
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 10, color: p.gradeColor, background: `${p.gradeColor}15`, border: `1px solid ${p.gradeColor}30` }}>{p.grade}</span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <p style={{ fontSize: 10, color: '#94a3b8', marginTop: 5 }}>Grey vertical marker on each bar = industry benchmark position.</p>
      </Section>

      {/* ── Section 2: Deep Dive on Each Area ── */}
      <Section num="2" title="Security Area Deep Dive">
        <p className="pdf-para">
          For each of the five security areas, we explain what we measure, what we are doing
          to protect the organisation, what is working well, and where we can improve.
        </p>
        {EXEC_POSTURE_PILLARS.map((p) => (
          <PosturePillarCard key={p.id} pillar={p} />
        ))}
      </Section>

      {/* ── Section 3: Value of Security Investment ── */}
      <Section num="3" title="The Value of Your Security Investment">
        <div style={{
          background: 'linear-gradient(135deg, #052e16 0%, #14532d 100%)',
          borderRadius: 10, padding: '20px 24px', color: '#fff', marginBottom: 16,
        }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#4ade80', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 10 }}>Return on Security Investment — {REPORT_PERIOD}</div>
          <p style={{ fontSize: 13, color: '#d1fae5', lineHeight: 1.7, margin: '0 0 16px' }}>{EXEC_POSTURE_ROI.statement}</p>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            {[
              { label: 'Threats Blocked',              value: EXEC_POSTURE_ROI.incidentsBlocked.toLocaleString(), color: '#4ade80' },
              { label: 'Avg. Cost per Incident',       value: `$${EXEC_POSTURE_ROI.estimatedCostPerIncident.toLocaleString()}`, color: '#fbbf24' },
              { label: 'Estimated Value Protected',    value: EXEC_POSTURE_ROI.estimatedValueProtected, color: '#4ade80' },
              { label: 'Platform Annual Cost',         value: EXEC_POSTURE_ROI.platformCostEstimate,   color: '#94a3b8' },
              { label: 'Return on Investment',         value: EXEC_POSTURE_ROI.roi,                    color: '#a78bfa' },
            ].map((k, i) => (
              <div key={i} style={{ flex: 1, minWidth: 100, background: 'rgba(255,255,255,0.07)', borderRadius: 6, padding: '10px 14px', textAlign: 'center' }}>
                <div style={{ fontSize: 20, fontWeight: 800, color: k.color, fontFamily: 'monospace' }}>{k.value}</div>
                <div style={{ fontSize: 9, color: '#86efac', textTransform: 'uppercase', letterSpacing: '0.07em', marginTop: 4 }}>{k.label}</div>
              </div>
            ))}
          </div>
        </div>
        <p style={{ fontSize: 11, color: '#94a3b8', fontStyle: 'italic' }}>
          * Incident cost estimates based on IBM Cost of a Data Breach Report 2024 averages for organisations of similar size and sector.
          Actual figures may vary. ROI calculation = Estimated Value Protected ÷ Annual Platform Cost.
        </p>
      </Section>

      {/* ── Section 4: Improvement Roadmap ── */}
      <Section num="4" title="Security Improvement Roadmap">
        <p className="pdf-para">
          The following four actions will have the greatest impact on improving your security
          posture. They are ordered by priority. Three of the four have no additional technology
          cost — they require configuration or process changes only.
        </p>
        {EXEC_POSTURE_ROADMAP.map((item, i) => (
          <div key={i} style={{
            border: `1px solid ${item.color}30`,
            borderLeft: `5px solid ${item.color}`,
            borderRadius: 8, padding: '12px 16px', marginBottom: 12, background: '#fff',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
              <span style={{ fontSize: 10, fontWeight: 800, padding: '3px 9px', borderRadius: 12, color: item.color, background: `${item.color}18`, border: `1px solid ${item.color}40`, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{item.priority}</span>
              <strong style={{ fontSize: 14, color: '#0f172a', flex: 1 }}>{item.action}</strong>
              <span style={{ fontSize: 11, color: '#94a3b8', whiteSpace: 'nowrap' }}>Target: {item.timeline}</span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div>
                <p style={{ fontSize: 12, color: '#334155', lineHeight: 1.6, margin: '0 0 6px' }}><strong>Business Case:</strong> {item.businessCase}</p>
                <p style={{ fontSize: 12, color: '#64748b', margin: 0 }}><strong>Owner:</strong> {item.owner}</p>
              </div>
              <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 6, padding: '8px 12px' }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: '#166534', textTransform: 'uppercase', marginBottom: 4 }}>Cost Estimate</div>
                <p style={{ fontSize: 12, color: '#166534', fontWeight: 600, margin: 0 }}>{item.cost}</p>
              </div>
            </div>
          </div>
        ))}
      </Section>

      {/* ── Section 5: Technical Appendix ── */}
      <Section num="5" title="Appendix: Technical Domain Scores">
        <p className="pdf-para" style={{ fontSize: 11, color: '#64748b' }}>
          Detailed domain scores for IT and security teams. Scores are derived from Azure Sentinel
          query telemetry, Microsoft Defender XDR policy coverage, and Entra ID configuration analysis.
        </p>
        <table className="pdf-table">
          <thead>
            <tr>
              <th>Domain</th>
              <th style={{width:'8%', textAlign:'center'}}>Score</th>
              <th style={{width:'8%', textAlign:'center'}}>Benchmark</th>
              <th style={{width:'8%', textAlign:'center'}}>Delta</th>
              <th style={{width:'30%'}}>Bar</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            {POSTURE_DOMAINS.map((d, i) => {
              const delta = d.score - d.benchmark;
              const c = d.score >= 80 ? '#059669' : d.score >= 65 ? '#ca8a04' : '#dc2626';
              return (
                <tr key={d.name} style={{ background: i % 2 === 0 ? '#fff' : '#f8fafc' }}>
                  <td style={{ fontWeight: 700 }}>{d.name}</td>
                  <td style={{ textAlign: 'center', fontWeight: 700, color: c }}>{d.score}</td>
                  <td style={{ textAlign: 'center', color: '#94a3b8' }}>{d.benchmark}</td>
                  <td style={{ textAlign: 'center', fontWeight: 700, color: delta >= 0 ? '#059669' : '#dc2626' }}>{delta >= 0 ? '+' : ''}{delta}</td>
                  <td>
                    <div style={{ position: 'relative' }}>
                      <InlineBar pct={d.score} color={c} height={7} />
                      <div style={{ position: 'absolute', top: -2, left: `${d.benchmark}%`, width: 2, height: 11, background: '#64748b', transform: 'translateX(-50%)' }} />
                    </div>
                  </td>
                  <td style={{ fontSize: 11, color: '#64748b' }}>{d.description}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Section>

      <Footer org={REPORT_ORG} />
    </div>
  );
}

// ─── 3. Incident Summary Report ───────────────────────────────────────────────

function IncidentSummaryReport() {
  const totals = INCIDENT_SEVERITY_BREAKDOWN.reduce((a, r) => ({ count: a.count + r.count, resolved: a.resolved + r.resolved, open: a.open + r.open }), { count: 0, resolved: 0, open: 0 });

  return (
    <div className="pdf-report">
      <Cover
        title="Incident Summary Report"
        subtitle={`${INCIDENT_WEEK} · ${INCIDENT_PERIOD} · Security Operations Centre`}
        accentColor="#2563eb"
        meta={[
          { label: 'Organization',     value: REPORT_ORG          },
          { label: 'Report Period',    value: INCIDENT_PERIOD      },
          { label: 'Report Week',      value: INCIDENT_WEEK        },
          { label: 'Data Source',      value: 'Azure Sentinel · Microsoft Defender XDR' },
          { label: 'Generated',        value: TODAY                },
          { label: 'Classification',   value: 'Confidential'       },
        ]}
      />

      <Section num="1" title="Executive Summary">
        <p className="pdf-para">
          During <strong>{INCIDENT_PERIOD}</strong>, the Security Operations Centre processed a
          total of <strong>{totals.count} security incidents</strong>, of which{' '}
          <strong>{totals.resolved} ({Math.round((totals.resolved / totals.count) * 100)}%) were
          fully resolved</strong> within the reporting week. <strong>{totals.open} incidents remain
          open</strong>, including 2 at Critical severity under active investigation. The Mean Time to
          Detect (MTTD) improved by 18% compared to the prior week, driven by new Sentinel analytic
          rules deployed on May 17, 2026.
        </p>
        <KpiGrid kpis={[
          { value: totals.count,                              label: 'Total Incidents',       color: '#0f172a' },
          { value: totals.resolved,                           label: 'Resolved',              color: '#059669' },
          { value: totals.open,                               label: 'Open',                  color: '#dc2626' },
          { value: INCIDENT_RESPONSE_METRICS.mttd,           label: 'MTTD',                  color: '#ca8a04' },
          { value: INCIDENT_RESPONSE_METRICS.mttr,           label: 'MTTR',                  color: '#0f172a' },
          { value: INCIDENT_RESPONSE_METRICS.autoContained,  label: 'Auto-Contained',        color: '#059669' },
          { value: INCIDENT_RESPONSE_METRICS.escalated,      label: 'Escalated',             color: '#ea580c' },
          { value: INCIDENT_RESPONSE_METRICS.falsePositiveRate, label: 'False Positive Rate', color: '#64748b' },
        ]} />
      </Section>

      <Section num="2" title="Incident Breakdown by Severity">
        <table className="pdf-table">
          <thead>
            <tr>
              <th>Severity</th>
              <th style={{width:'10%', textAlign:'center'}}>Total</th>
              <th style={{width:'10%', textAlign:'center'}}>Resolved</th>
              <th style={{width:'10%', textAlign:'center'}}>Open</th>
              <th style={{width:'8%', textAlign:'center'}}>% of Total</th>
              <th style={{width:'25%'}}>Volume</th>
            </tr>
          </thead>
          <tbody>
            {INCIDENT_SEVERITY_BREAKDOWN.map((row) => (
              <tr key={row.severity}>
                <td><SevBadge sev={row.severity} /></td>
                <td style={{ textAlign: 'center', fontWeight: 700 }}>{row.count}</td>
                <td style={{ textAlign: 'center', color: '#059669', fontWeight: 600 }}>{row.resolved}</td>
                <td style={{ textAlign: 'center', color: row.open > 0 ? '#dc2626' : '#059669', fontWeight: 600 }}>{row.open}</td>
                <td style={{ textAlign: 'center', color: '#64748b' }}>{row.pct}%</td>
                <td><InlineBar pct={row.pct} color={SEV_COLOR[row.severity]} height={8} /></td>
              </tr>
            ))}
            <tr style={{ background: '#f1f5f9', fontWeight: 700 }}>
              <td>Total</td>
              <td style={{ textAlign: 'center' }}>{totals.count}</td>
              <td style={{ textAlign: 'center', color: '#059669' }}>{totals.resolved}</td>
              <td style={{ textAlign: 'center', color: '#dc2626' }}>{totals.open}</td>
              <td style={{ textAlign: 'center' }}>100%</td>
              <td></td>
            </tr>
          </tbody>
        </table>
      </Section>

      <Section num="3" title="Incident Breakdown by Category">
        <table className="pdf-table">
          <thead>
            <tr>
              <th>Incident Category</th>
              <th style={{width:'10%', textAlign:'center'}}>Count</th>
              <th style={{width:'8%', textAlign:'center'}}>%</th>
              <th style={{width:'30%'}}>Distribution</th>
            </tr>
          </thead>
          <tbody>
            {INCIDENT_CATEGORY_BREAKDOWN.sort((a, b) => b.count - a.count).map((c, i) => (
              <tr key={c.category} style={{ background: i % 2 === 0 ? '#fff' : '#f8fafc' }}>
                <td style={{ fontWeight: 500 }}>{c.category}</td>
                <td style={{ textAlign: 'center', fontWeight: 700 }}>{c.count}</td>
                <td style={{ textAlign: 'center', color: '#64748b' }}>{c.pct}%</td>
                <td><InlineBar pct={c.pct * 5} color="#2563eb" height={7} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </Section>

      <Section num="4" title="Daily Incident Trend – Week 21">
        <table className="pdf-table">
          <thead>
            <tr>
              <th>Date</th>
              <th style={{width:'9%', textAlign:'center'}}>Critical</th>
              <th style={{width:'9%', textAlign:'center'}}>High</th>
              <th style={{width:'9%', textAlign:'center'}}>Medium</th>
              <th style={{width:'9%', textAlign:'center'}}>Low</th>
              <th style={{width:'9%', textAlign:'center'}}>Total</th>
            </tr>
          </thead>
          <tbody>
            {WEEKLY_INCIDENT_TREND.map((d, i) => {
              const total = d.critical + d.high + d.medium + d.low;
              return (
                <tr key={d.day} style={{ background: i % 2 === 0 ? '#fff' : '#f8fafc' }}>
                  <td style={{ fontSize: 12 }}>{d.day}</td>
                  <td style={{ textAlign: 'center', color: d.critical > 0 ? '#dc2626' : '#94a3b8', fontWeight: d.critical > 0 ? 700 : 400 }}>{d.critical}</td>
                  <td style={{ textAlign: 'center', color: d.high > 0 ? '#ea580c' : '#94a3b8' }}>{d.high}</td>
                  <td style={{ textAlign: 'center', color: '#ca8a04' }}>{d.medium}</td>
                  <td style={{ textAlign: 'center', color: '#16a34a' }}>{d.low}</td>
                  <td style={{ textAlign: 'center', fontWeight: 700 }}>{total}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Section>

      <Section num="5" title="Notable Incidents – Detailed Review">
        <p className="pdf-para" style={{marginBottom: 12}}>
          The following incidents were selected for detailed review based on severity, business
          impact, or novel attack technique. All times are UTC.
        </p>
        {TOP_INCIDENTS.map((inc, i) => (
          <div key={inc.id} style={{ border: '1px solid #e2e8f0', borderRadius: 8, marginBottom: 14, overflow: 'hidden' }}>
            <div style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0', padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontFamily: 'monospace', fontWeight: 700, fontSize: 11, color: '#64748b' }}>{inc.id}</span>
              <SevBadge sev={inc.severity} />
              <strong style={{ flex: 1, fontSize: 13, color: '#0f172a' }}>{inc.title}</strong>
              <span style={{ fontSize: 11, color: inc.status === 'Investigating' ? '#ea580c' : '#059669', fontWeight: 600 }}>● {inc.status}</span>
            </div>
            <div style={{ padding: '10px 14px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 24px', fontSize: 12 }}>
              <div><strong style={{color:'#475569'}}>Category:</strong> {inc.category}</div>
              <div><strong style={{color:'#475569'}}>MITRE Ref:</strong> <span style={{fontFamily:'monospace',fontSize:11}}>{inc.mitreRef}</span></div>
              <div><strong style={{color:'#475569'}}>Detected:</strong> {inc.detectedAt}</div>
              <div><strong style={{color:'#475569'}}>Resolved:</strong> {inc.resolvedAt || <span style={{color:'#ea580c'}}>Ongoing</span>}</div>
              <div><strong style={{color:'#475569'}}>Affected Assets:</strong> {inc.affectedAssets.join(', ')}</div>
              <div><strong style={{color:'#475569'}}>Affected Users:</strong> {inc.affectedUsers.join(', ')}</div>
              {inc.mttr && <div><strong style={{color:'#475569'}}>MTTR:</strong> <span style={{color:'#059669',fontWeight:600}}>{inc.mttr}</span></div>}
            </div>
            <div style={{ padding: '8px 14px 12px', borderTop: '1px solid #f1f5f9' }}>
              <p style={{ fontSize: 12, color: '#475569', lineHeight: 1.6, margin: 0 }}><strong>Analysis:</strong> {inc.summary}</p>
            </div>
          </div>
        ))}
      </Section>

      <Section num="6" title="Response Performance Metrics">
        <KpiGrid kpis={[
          { value: INCIDENT_RESPONSE_METRICS.mttd,              label: 'Mean Time to Detect',      color: '#ca8a04' },
          { value: INCIDENT_RESPONSE_METRICS.mttr,              label: 'Mean Time to Respond',     color: '#0f172a' },
          { value: INCIDENT_RESPONSE_METRICS.autoContained,     label: 'Auto-Contained Rate',      color: '#059669' },
          { value: INCIDENT_RESPONSE_METRICS.escalated,         label: 'Escalated to Tier 3',      color: '#ea580c' },
          { value: INCIDENT_RESPONSE_METRICS.slaBreached,       label: 'SLA Breached',             color: '#dc2626' },
          { value: INCIDENT_RESPONSE_METRICS.falsePositiveRate, label: 'False Positive Rate',      color: '#64748b' },
        ]} />
        <p className="pdf-para" style={{marginTop: 12}}>
          SLA breach root cause: Two Critical incidents (INC-20240628, INC-20240589) exceeded the
          4-hour Critical SLA due to forensic hold delays. Three High incidents breached the 24-hour
          SLA due to off-hours detection without on-call coverage. Action: expand on-call rotation to
          cover weekends.
        </p>
      </Section>

      <Footer org={REPORT_ORG} />
    </div>
  );
}

// ─── 4. Compliance Audit Report ───────────────────────────────────────────────

function ComplianceAuditReport() {
  const avgScore = Math.round(COMPLIANCE_FRAMEWORKS.reduce((s, f) => s + f.score, 0) / COMPLIANCE_FRAMEWORKS.length);
  const totalFindings = COMPLIANCE_FINDINGS.length;
  const criticalFindings = COMPLIANCE_FINDINGS.filter(f => f.severity === 'Critical').length;

  return (
    <div className="pdf-report">
      <Cover
        title="Compliance Audit Report"
        subtitle="NIST CSF 2.0 · ISO/IEC 27001:2022 · CIS Controls v8 · GDPR"
        accentColor="#7c3aed"
        meta={[
          { label: 'Organization',      value: REPORT_ORG           },
          { label: 'Audit Period',      value: COMPLIANCE_PERIOD    },
          { label: 'Frameworks',        value: 'NIST CSF 2.0, ISO/IEC 27001:2022, CIS v8, GDPR' },
          { label: 'Average Score',     value: `${avgScore}/100`    },
          { label: 'Generated',         value: TODAY                },
          { label: 'Classification',    value: 'Confidential'       },
        ]}
      />

      <Section num="1" title="Executive Summary">
        <p className="pdf-para">
          This compliance audit covers <strong>{COMPLIANCE_PERIOD}</strong> across four security
          frameworks. <strong>{REPORT_ORG}</strong> achieved an average compliance score of{' '}
          <strong>{avgScore}/100</strong>. The strongest compliance posture is observed against
          GDPR (88/100), reflecting mature data protection practices. The weakest area is
          CIS Controls v8 (71/100), with 17 failed controls primarily in network security and
          configuration management.
        </p>
        <p className="pdf-para">
          <strong>{totalFindings} audit findings</strong> were identified, including{' '}
          <strong style={{color:'#dc2626'}}>{criticalFindings} Critical</strong> and{' '}
          <strong style={{color:'#ea580c'}}>{COMPLIANCE_FINDINGS.filter(f => f.severity === 'High').length} High</strong>{' '}
          severity items requiring immediate remediation. A detailed remediation roadmap with
          target dates and ownership is provided in Section 6.
        </p>
        <KpiGrid kpis={[
          { value: avgScore,             label: 'Average Score',      color: '#7c3aed', sub: 'Across 4 frameworks' },
          { value: COMPLIANCE_FRAMEWORKS.reduce((s,f) => s + f.controls, 0), label: 'Total Controls', color: '#0f172a' },
          { value: COMPLIANCE_FRAMEWORKS.reduce((s,f) => s + f.passed, 0),   label: 'Controls Passed', color: '#059669' },
          { value: COMPLIANCE_FRAMEWORKS.reduce((s,f) => s + f.failed, 0),   label: 'Controls Failed', color: '#dc2626' },
          { value: criticalFindings,     label: 'Critical Findings',  color: '#dc2626' },
          { value: totalFindings,        label: 'Total Findings',     color: '#0f172a' },
        ]} />
      </Section>

      <Section num="2" title="Framework Compliance Summary">
        <table className="pdf-table">
          <thead>
            <tr>
              <th>Framework</th>
              <th style={{width:'9%', textAlign:'center'}}>Score</th>
              <th style={{width:'9%', textAlign:'center'}}>Controls</th>
              <th style={{width:'9%', textAlign:'center'}}>Passed</th>
              <th style={{width:'9%', textAlign:'center'}}>Partial</th>
              <th style={{width:'9%', textAlign:'center'}}>Failed</th>
              <th style={{width:'22%'}}>Compliance Level</th>
            </tr>
          </thead>
          <tbody>
            {COMPLIANCE_FRAMEWORKS.map((f) => {
              const c = f.score >= 85 ? '#059669' : f.score >= 70 ? '#ca8a04' : '#dc2626';
              return (
                <tr key={f.id}>
                  <td style={{ fontWeight: 700 }}>{f.name}</td>
                  <td style={{ textAlign: 'center', fontWeight: 700, color: c }}>{f.score}</td>
                  <td style={{ textAlign: 'center' }}>{f.controls}</td>
                  <td style={{ textAlign: 'center', color: '#059669', fontWeight: 600 }}>{f.passed}</td>
                  <td style={{ textAlign: 'center', color: '#ca8a04', fontWeight: 600 }}>{f.partial}</td>
                  <td style={{ textAlign: 'center', color: '#dc2626', fontWeight: 600 }}>{f.failed}</td>
                  <td><InlineBar pct={f.score} color={c} height={8} /></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Section>

      <Section num="3" title="NIST CSF 2.0 – Function-Level Assessment">
        <p className="pdf-para" style={{marginBottom: 12}}>
          The NIST Cybersecurity Framework 2.0 is assessed across six core functions. The
          Recover function (68/100) is the lowest-scoring area, flagging inadequate BCP testing
          and absence of formal recovery playbooks.
        </p>
        <table className="pdf-table">
          <thead>
            <tr>
              <th>CSF Function</th>
              <th style={{width:'10%', textAlign:'center'}}>Score</th>
              <th style={{width:'25%'}}>Visual</th>
              <th>Assessment Notes</th>
            </tr>
          </thead>
          <tbody>
            {NIST_FUNCTIONS.map((fn, i) => {
              const c = fn.score >= 80 ? '#059669' : fn.score >= 65 ? '#ca8a04' : '#dc2626';
              return (
                <tr key={fn.fn} style={{ background: i % 2 === 0 ? '#fff' : '#f8fafc' }}>
                  <td style={{ fontWeight: 700, color: '#7c3aed' }}>{fn.fn}</td>
                  <td style={{ textAlign: 'center', fontWeight: 700, color: c }}>{fn.score}</td>
                  <td><InlineBar pct={fn.score} color={c} height={7} /></td>
                  <td style={{ fontSize: 11, color: '#64748b' }}>{fn.description}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Section>

      <Section num="4" title="Audit Findings">
        <p className="pdf-para" style={{marginBottom: 12}}>
          All findings were identified through automated Sentinel policy assessment, manual
          evidence review, and configuration analysis conducted during the audit period.
        </p>
        {COMPLIANCE_FINDINGS.map((f, i) => (
          <div key={f.id} style={{
            border: '1px solid #e2e8f0',
            borderLeft: `4px solid ${SEV_COLOR[f.severity] || '#64748b'}`,
            borderRadius: 6, marginBottom: 14, overflow: 'hidden',
          }}>
            <div style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0', padding: '9px 14px', display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontFamily: 'monospace', fontWeight: 700, fontSize: 11, color: '#64748b' }}>{f.id}</span>
              <SevBadge sev={f.severity} />
              <span style={{ fontSize: 11, color: '#7c3aed', fontWeight: 600, background: '#f3e8ff', border: '1px solid #e9d5ff', padding: '1px 7px', borderRadius: 4 }}>{f.framework}</span>
              <strong style={{ flex: 1, fontSize: 12, color: '#0f172a' }}>{f.control}</strong>
            </div>
            <div style={{ padding: '10px 14px' }}>
              <p style={{ fontSize: 12, color: '#475569', marginBottom: 6, lineHeight: 1.55 }}>
                <strong>Finding:</strong> {f.finding}
              </p>
              <p style={{ fontSize: 12, color: '#dc2626', marginBottom: 6, lineHeight: 1.55 }}>
                <strong>Risk:</strong> {f.risk}
              </p>
              <p style={{ fontSize: 12, color: '#059669', marginBottom: 6, lineHeight: 1.55 }}>
                <strong>Recommendation:</strong> {f.recommendation}
              </p>
              <div style={{ display: 'flex', gap: 24, fontSize: 11, color: '#94a3b8' }}>
                <span><strong>Target Date:</strong> {f.remediation}</span>
                <span><strong>Owner:</strong> {f.owner}</span>
              </div>
            </div>
          </div>
        ))}
      </Section>

      <Section num="5" title="Findings Summary Matrix">
        <table className="pdf-table">
          <thead>
            <tr>
              <th>Finding ID</th>
              <th>Control</th>
              <th style={{width:'8%', textAlign:'center'}}>Severity</th>
              <th style={{width:'10%', textAlign:'center'}}>Framework</th>
              <th style={{width:'12%', textAlign:'center'}}>Target Date</th>
              <th>Owner</th>
            </tr>
          </thead>
          <tbody>
            {COMPLIANCE_FINDINGS.map((f, i) => (
              <tr key={f.id} style={{ background: i % 2 === 0 ? '#fff' : '#f8fafc' }}>
                <td style={{ fontFamily: 'monospace', fontWeight: 700, fontSize: 11 }}>{f.id}</td>
                <td style={{ fontSize: 11 }}>{f.control.split(' – ')[0]}</td>
                <td style={{ textAlign: 'center' }}><SevBadge sev={f.severity} /></td>
                <td style={{ textAlign: 'center', fontSize: 10, color: '#7c3aed', fontWeight: 600 }}>{f.framework}</td>
                <td style={{ textAlign: 'center', fontSize: 11 }}>{f.remediation}</td>
                <td style={{ fontSize: 11, color: '#64748b' }}>{f.owner}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Section>

      <Section num="6" title="Remediation Roadmap">
        <p className="pdf-para" style={{marginBottom: 12}}>
          Remediation items are phased across three quarters. Critical and High findings from this
          audit are assigned to Q2 2026 with named owners. Progress will be tracked in VorXOC's
          compliance dashboard.
        </p>
        {COMPLIANCE_REMEDIATION_ROADMAP.map((q, i) => (
          <div key={q.quarter} style={{ marginBottom: 14 }}>
            <div style={{ background: i === 0 ? '#7c3aed' : '#f1f5f9', color: i === 0 ? '#fff' : '#0f172a', padding: '7px 14px', borderRadius: '6px 6px 0 0', fontWeight: 700, fontSize: 13 }}>
              {q.quarter} {i === 0 && '← Active Quarter'}
            </div>
            <div style={{ border: '1px solid #e2e8f0', borderTop: 'none', borderRadius: '0 0 6px 6px', padding: '10px 14px' }}>
              {q.items.map((item, j) => (
                <div key={j} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '4px 0', borderBottom: j < q.items.length - 1 ? '1px solid #f1f5f9' : 'none', fontSize: 12, color: '#334155' }}>
                  <span style={{ color: i === 0 ? '#7c3aed' : '#94a3b8', fontWeight: 700, flexShrink: 0 }}>◆</span>
                  {item}
                </div>
              ))}
            </div>
          </div>
        ))}
      </Section>

      <Footer org={REPORT_ORG} />
    </div>
  );
}

// ─── Print CSS ────────────────────────────────────────────────────────────────

const PRINT_CSS = `
* { margin: 0; padding: 0; box-sizing: border-box; }
body { font-family: 'Segoe UI', Arial, sans-serif; color: #1e293b; background: #fff; }
.pdf-report { max-width: 860px; margin: 0 auto; padding: 28px 36px; }

/* Cover */
.pdf-cover { position: relative; min-height: 420px; padding: 0; margin-bottom: 48px; display: flex; flex-direction: column; }
.pdf-cover__accent { height: 8px; background: #059669; width: 100%; margin-bottom: 0; }
.pdf-cover__inner { padding: 48px 40px 48px; border: 1px solid #e2e8f0; border-top: none; flex: 1; display: flex; flex-direction: column; gap: 28px; }
.pdf-cover__brand { display: flex; flex-direction: column; gap: 4px; }
.pdf-cover__logo { font-size: 28px; font-weight: 900; letter-spacing: 0.12em; color: #059669; }
.pdf-cover__tagline { font-size: 9px; font-weight: 700; letter-spacing: 0.2em; color: #94a3b8; text-transform: uppercase; }
.pdf-cover__titles { }
.pdf-cover__title { font-size: 30px; font-weight: 800; color: #0f172a; margin-bottom: 8px; line-height: 1.2; }
.pdf-cover__subtitle { font-size: 13px; color: #64748b; }
.pdf-cover__meta-block { display: inline-flex; flex-direction: column; gap: 6px; background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 8px; padding: 14px 20px; align-self: flex-start; }
.pdf-cover__meta-row { display: flex; gap: 12px; font-size: 12px; }
.pdf-cover__meta-key { color: #64748b; min-width: 140px; }
.pdf-cover__meta-val { color: #1e293b; font-weight: 600; }
.pdf-cover__confidential { font-size: 9px; font-weight: 700; letter-spacing: 0.15em; text-transform: uppercase; color: #dc2626; border: 1px solid #fecaca; background: #fff7f7; padding: 4px 12px; border-radius: 4px; align-self: flex-start; }

/* Sections */
.pdf-section { margin-bottom: 36px; }
.pdf-section__hdr { display: flex; align-items: center; gap: 10px; border-bottom: 2px solid #059669; padding-bottom: 8px; margin-bottom: 14px; }
.pdf-section__num { width: 24px; height: 24px; background: #059669; color: #fff; border-radius: 50%; font-size: 11px; font-weight: 800; display: inline-flex; align-items: center; justify-content: center; flex-shrink: 0; }
.pdf-section__title { font-size: 17px; font-weight: 700; color: #0f172a; }
.pdf-section__body { }
.pdf-para { font-size: 13px; line-height: 1.7; color: #475569; margin-bottom: 12px; }

/* KPI grid */
.pdf-kpi-grid { display: flex; flex-wrap: wrap; gap: 10px; margin: 12px 0; }
.pdf-kpi { flex: 1; min-width: 100px; border: 1px solid #e2e8f0; border-top: 3px solid #059669; border-radius: 8px; padding: 12px 14px; background: #fafcff; }
.pdf-kpi__value { font-size: 20px; font-weight: 800; color: #0f172a; font-family: 'Courier New', monospace; }
.pdf-kpi__label { font-size: 10px; color: #94a3b8; margin-top: 3px; text-transform: uppercase; letter-spacing: 0.05em; font-weight: 600; }
.pdf-kpi__sub { font-size: 10px; color: #059669; font-weight: 600; margin-top: 2px; }

/* Tables */
.pdf-table { width: 100%; border-collapse: collapse; font-size: 12px; margin-bottom: 8px; }
.pdf-table th { background: #1e293b; color: #f8fafc; font-weight: 700; padding: 8px 10px; text-align: left; font-size: 10px; text-transform: uppercase; letter-spacing: 0.05em; }
.pdf-table td { padding: 8px 10px; border-bottom: 1px solid #f1f5f9; color: #334155; vertical-align: middle; }

/* Recommendations */
.pdf-rec { border: 1px solid #e2e8f0; border-radius: 8px; padding: 12px 14px; margin-bottom: 10px; }
.pdf-rec__header { display: flex; align-items: center; gap: 8px; margin-bottom: 6px; flex-wrap: wrap; }
.pdf-rec__num { width: 22px; height: 22px; background: #1e293b; color: #fff; border-radius: 50%; font-size: 10px; font-weight: 800; display: inline-flex; align-items: center; justify-content: center; flex-shrink: 0; }
.pdf-rec__rationale { font-size: 12px; color: #64748b; line-height: 1.55; margin-bottom: 6px; padding-left: 30px; }
.pdf-rec__meta { display: flex; gap: 20px; font-size: 11px; color: #94a3b8; padding-left: 30px; }
.pdf-rec__meta strong { color: #475569; }

/* Footer */
.pdf-footer { margin-top: 48px; padding-top: 14px; border-top: 2px solid #e2e8f0; display: flex; justify-content: space-between; font-size: 10px; color: #94a3b8; font-weight: 600; text-transform: uppercase; letter-spacing: 0.06em; }

@media print {
  body { print-color-adjust: exact; -webkit-print-color-adjust: exact; }
  .pdf-cover { page-break-after: always; }
  .pdf-section { page-break-inside: avoid; }
  .pdf-rec { page-break-inside: avoid; }
}
`;

// ─── Report Titles ────────────────────────────────────────────────────────────

const REPORT_TITLES = {
  mitre:      'MITRE ATT&CK Security Assessment',
  posture:    'Security Posture Report',
  incident:   'Incident Summary Report',
  compliance: 'Compliance Audit Report',
};

// ─── Modal Shell ──────────────────────────────────────────────────────────────

export default function ReportPreviewModal({ reportType, onClose }) {
  const printRef = useRef(null);
  const [downloading, setDownloading] = useState(false);

  // ── Browser print dialog (existing behaviour) ──────────────────────────
  const handlePrint = () => {
    const content = printRef.current?.innerHTML;
    if (!content) return;
    const win = window.open('', '_blank');
    win.document.write(`<!DOCTYPE html><html><head>
      <title>${REPORT_TITLES[reportType] || 'Security Report'} — ${REPORT_ORG}</title>
      <style>${PRINT_CSS}</style>
    </head><body>${content}</body></html>`);
    win.document.close();
    setTimeout(() => win.print(), 500);
  };

  // ── Frontend PDF download via html2pdf.js ──────────────────────────────
  const handleDownloadPdf = async () => {
    if (!printRef.current || downloading) return;
    setDownloading(true);
    try {
      const html2pdf = (await import('html2pdf.js')).default;
      const reportTitle = REPORT_TITLES[reportType] || 'Security-Report';
      const filename = `${reportTitle.replace(/\s+/g, '-')}_${REPORT_ORG.replace(/\s+/g, '-')}_${TODAY.replace(/,/g, '').replace(/\s+/g, '-')}.pdf`;

      const opt = {
        margin:      [12, 12, 12, 12],   // top, right, bottom, left (mm)
        filename,
        image:       { type: 'jpeg', quality: 0.98 },
        html2canvas: {
          scale: 2,                       // retina-quality
          useCORS: true,
          logging: false,
          backgroundColor: '#ffffff',
        },
        jsPDF: {
          unit: 'mm',
          format: 'a4',
          orientation: 'portrait',
        },
        pagebreak: { mode: ['avoid-all', 'css', 'legacy'] },
      };

      await html2pdf().set(opt).from(printRef.current).save();
    } catch (err) {
      console.error('PDF generation failed:', err);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="report-modal-overlay" onClick={onClose}>
      <div className="report-modal" onClick={(e) => e.stopPropagation()}>
        <div className="report-modal__header">
          <h2 className="report-modal__title">{REPORT_TITLES[reportType]}</h2>
          <div className="report-modal__actions">
            <button
              className="btn btn--outline btn--sm"
              onClick={handleDownloadPdf}
              disabled={downloading}
              title="Download as PDF"
            >
              {downloading
                ? <><Loader2 size={13} className="spin" /> Generating…</>
                : <><Download size={13} /> Download PDF</>
              }
            </button>
            <button
              className="report-modal__close"
              onClick={onClose}
              aria-label="Close report preview"
              type="button"
            >
              <X size={16} />
            </button>
          </div>
        </div>
        <div className="report-modal__body">
          <div ref={printRef}>
            {reportType === 'mitre'      && <MitreAssessmentReport />}
            {reportType === 'posture'    && <SecurityPostureReport  />}
            {reportType === 'incident'   && <IncidentSummaryReport  />}
            {reportType === 'compliance' && <ComplianceAuditReport  />}
          </div>
        </div>
      </div>
    </div>
  );
}
